/**
 * Wandelt Quill-spezifisches HTML in standardkonformes HTML um.
 * Splittet gemischte ol-Listen mit data-list-Attributen in saubere ol-/ul-Listen,
 * entfernt Quill-UI-Spans, konvertiert ql-align- und ql-indent-Klassen in Inline-Styles
 * und räumt nicht mehr benötigte Attribute weg.
 * Das Ergebnis rendert in beliebigen Kontexten (E-Mail-Clients, andere Web-Anzeigen)
 * korrekt, ohne dass Quill-CSS verfügbar sein muss.
 */
export function normalizeQuillHtml(html: string = ''): string {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild as HTMLElement;

    // Gemischte ol-Listen (ordered/bullet) in saubere ol-/ul-Listen aufteilen
    root.querySelectorAll('ol').forEach(ol => splitMixedList(ol));

    // Quill-interne UI-Spans entfernen, die außerhalb des Editors keinen Zweck haben
    root.querySelectorAll('span.ql-ui').forEach(span => span.remove());

    // data-list-Attribute entfernen (werden nach dem Split nicht mehr benötigt)
    root.querySelectorAll('[data-list]').forEach(el => el.removeAttribute('data-list'));

    // contenteditable-Attribute entfernen (Editor-spezifisch, nicht für die Anzeige relevant)
    root.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));

    // Externe Links ohne Schema zu absoluten URLs umschreiben (verhindert relative Pfade)
    root.querySelectorAll('a[href]').forEach(a => normalizeAnchorHref(a as HTMLAnchorElement));

    // ql-align-Klassen in text-align-Inline-Styles konvertieren
    convertQuillAlignToStyle(root);

    // ql-indent-Klassen in margin-left-Inline-Styles konvertieren
    convertQuillIndentToStyle(root);

    // ql-size-Klassen in font-size-Inline-Styles konvertieren
    convertQuillSizeToStyle(root);

    // ql-font-Klassen in font-family-Inline-Styles konvertieren
    convertQuillFontToStyle(root);

    return root.innerHTML;
}

/**
 * Teilt ein ol-Element mit gemischten data-list-Attributen (ordered/bullet)
 * in mehrere zusammenhängende ol- und ul-Listen auf.
 * Hintergrund: Quill verwendet immer ol als Container und unterscheidet die
 * Listenart über das data-list-Attribut auf dem li. Das ist nicht standardkonform
 * und führt in normalen Browsern zu fortlaufender Nummerierung statt Bullets.
 */
function splitMixedList(ol: HTMLOListElement): void {
    const items = Array.from(ol.children) as HTMLLIElement[];
    if (items.length === 0) return;

    const groups: { type: 'ol' | 'ul'; items: HTMLLIElement[] }[] = [];
    let currentGroup: { type: 'ol' | 'ul'; items: HTMLLIElement[] } | null = null;

    for (const li of items) {
        const listType = li.getAttribute('data-list') === 'bullet' ? 'ul' : 'ol';
        if (!currentGroup || currentGroup.type !== listType) {
            currentGroup = { type: listType, items: [] };
            groups.push(currentGroup);
        }
        currentGroup.items.push(li);
    }

    if (groups.length === 1 && groups[0].type === 'ol') return;

    const fragment = document.createDocumentFragment();
    for (const group of groups) {
        const list = document.createElement(group.type);
        group.items.forEach(item => list.appendChild(item));
        fragment.appendChild(list);
    }
    ol.replaceWith(fragment);
}

/**
 * Externe Links ohne Schema (z.B. "www.google.at") zu absoluten URLs umschreiben,
 * damit sie nicht als relative Pfade zur aktuellen Seite interpretiert werden.
 * Hintergrund: Quill schreibt User-Eingaben unverändert ins href-Attribut.
 * Ein href="www.google.at" wird vom Browser als relativer Pfad behandelt
 * und führt zu einer Weiterleitung auf z.B. https://meine-app.at/www.google.at
 * statt zur eigentlichen Zielseite.
 */
function normalizeAnchorHref(anchor: HTMLAnchorElement): void {
    const href = anchor.getAttribute('href') ?? '';
    if (!href) return;

    // Schemas und spezielle Präfixe, die in Ruhe gelassen werden
    const trustedPrefixes = ['http://', 'https://', 'mailto:', 'tel:', '#', '/'];
    const hasValidPrefix = trustedPrefixes.some(p => href.toLowerCase().startsWith(p));

    if (!hasValidPrefix) {
        anchor.setAttribute('href', `https://${href}`);
    }
}

/**
 * Wandelt Quill-spezifische ql-align-*-Klassen (center, right, justify) in
 * standardkonforme text-align-Inline-Styles um, damit die Ausrichtung auch
 * ohne Quill-CSS in E-Mail-Clients und anderen Anzeigen korrekt rendert.
 * @param root Das Wurzelelement, in dem nach ql-align-Klassen gesucht wird.
 */
function convertQuillAlignToStyle(root: HTMLElement): void {
    root.querySelectorAll('[class*="ql-align-"]').forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.classList.contains('ql-align-center')) htmlEl.style.textAlign = 'center';
        if (htmlEl.classList.contains('ql-align-right')) htmlEl.style.textAlign = 'right';
        if (htmlEl.classList.contains('ql-align-justify')) htmlEl.style.textAlign = 'justify';
        Array.from(htmlEl.classList)
            .filter(c => c.startsWith('ql-align-'))
            .forEach(c => htmlEl.classList.remove(c));
        if (htmlEl.classList.length === 0) htmlEl.removeAttribute('class');
    });
}

/**
 * Wandelt Quill-spezifische ql-indent-N-Klassen in standardkonforme
 * margin-left-Inline-Styles um. Pro Stufe werden 3em Einzug verwendet,
 * was dem Quill-Default entspricht. Damit funktioniert die Einrückung
 * auch in E-Mail-Clients und anderen Anzeigen ohne Quill-CSS.
 * @param root Das Wurzelelement, in dem nach ql-indent-Klassen gesucht wird.
 */
function convertQuillIndentToStyle(root: HTMLElement): void {
    root.querySelectorAll('[class*="ql-indent-"]').forEach(el => {
        const htmlEl = el as HTMLElement;
        const indentClass = Array.from(htmlEl.classList).find(c => c.startsWith('ql-indent-'));
        if (!indentClass) return;
        const level = parseInt(indentClass.replace('ql-indent-', ''), 10);
        if (!isNaN(level) && level > 0) htmlEl.style.marginLeft = `${level * 3}em`;
        htmlEl.classList.remove(indentClass);
        if (htmlEl.classList.length === 0) htmlEl.removeAttribute('class');
    });
}

/**
 * Wandelt Quill-spezifische ql-size-Klassen (small, large, huge) in
 * standardkonforme font-size-Inline-Styles um, damit die Schriftgrößen
 * auch ohne Quill-CSS in E-Mail-Clients und anderen Anzeigen korrekt rendern.
 * Die Pixel-Werte entsprechen den Quill-Defaults.
 * @param root Das Wurzelelement, in dem nach ql-size-Klassen gesucht wird.
 */
function convertQuillSizeToStyle(root: HTMLElement): void {
    root.querySelectorAll('[class*="ql-size-"]').forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.classList.contains('ql-size-small')) htmlEl.style.fontSize = '0.75em';
        if (htmlEl.classList.contains('ql-size-large')) htmlEl.style.fontSize = '1.5em';
        if (htmlEl.classList.contains('ql-size-huge')) htmlEl.style.fontSize = '2.5em';
        Array.from(htmlEl.classList)
            .filter(c => c.startsWith('ql-size-'))
            .forEach(c => htmlEl.classList.remove(c));
        if (htmlEl.classList.length === 0) htmlEl.removeAttribute('class');
    });
}

/**
 * Wandelt Quill-spezifische ql-font-*-Klassen in standardkonforme
 * font-family-Inline-Styles um, damit die Schriftart auch ohne Quill-CSS
 * in E-Mail-Clients und anderen Anzeigen korrekt rendert.
 * Standardmäßig unterstützt Quill 'serif' und 'monospace' (zusätzlich zur
 * Default-Sans-Serif). Die Werte sind generische Schriftfamilien, damit
 * jeder Mail-Client einen passenden Fallback findet.
 * @param root Das Wurzelelement, in dem nach ql-font-Klassen gesucht wird.
 */
function convertQuillFontToStyle(root: HTMLElement): void {
    root.querySelectorAll('[class*="ql-font-"]').forEach(el => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.classList.contains('ql-font-serif')) htmlEl.style.fontFamily = 'Georgia, "Times New Roman", serif';
        if (htmlEl.classList.contains('ql-font-monospace')) htmlEl.style.fontFamily = 'Consolas, "Courier New", monospace';
        Array.from(htmlEl.classList)
            .filter(c => c.startsWith('ql-font-'))
            .forEach(c => htmlEl.classList.remove(c));
        if (htmlEl.classList.length === 0) htmlEl.removeAttribute('class');
    });
}